import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from 'Auth';
import { useLazyIncentiveProgramsApi } from 'Shared/services/Api';
import { useRewardsEnrollmentContext } from 'Shared/providers/RewardsEnrollmentProvider';
import { reportEvent } from 'Shared/analytics';
import { env } from 'Shared/constants/env';
import {
  GetRewardsConfigResponse,
  OutcomeConfig,
  outcomesForPhase,
} from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { GetAgentStatusResponse } from 'Rewards/services/endpoints/GetAgentStatus/types';
import { PostAttemptResponse } from 'Rewards/services/endpoints/PostAttempt/types';
import { WheelCanvasHandle } from './components/WheelCanvas/WheelCanvas';
import { findMatchingSegmentIndexes } from './wheelUtils';

// ─── State machine ────────────────────────────────────────────────────────────
//
//  loading ──► idle       (config + status fetched, entries available)
//  loading ──► no_entries (config + status fetched, entries exhausted)
//  loading ──► error      (API error on initial load)
//  idle    ──► spinning   (user clicks Spin; POST /attempts fires)
//  spinning──► revealed   (wheel onRest fires; outcome modal opens)
//  revealed──► posting    (user clicks Play Another Entry)
//  posting ──► idle       (POST /attempts resolves; new pending outcome ready)
//  posting ──► idle       (POST /attempts errors; user can retry)
//  revealed──► idle       (user dismisses modal; entries remain)
//  revealed──► no_entries (user dismisses modal; entries exhausted)
//
//  'posting' is a sentinel that prevents the initial-data useEffect from
//  re-hydrating stale status while a new POST /attempts is in flight.
//  It renders identically to 'loading' in the UI (spinner shown).

export type SpinState =
  | 'loading'
  | 'posting'
  | 'idle'
  | 'spinning'
  | 'revealed'
  | 'no_entries'
  | 'error';

export type UseSpinWheelReturn = {
  config: GetRewardsConfigResponse | null;
  /** Outcomes for the currently-active TRACK1 phase, display-ready. */
  outcomes: OutcomeConfig[];
  status: GetAgentStatusResponse | null;
  spinState: SpinState;
  pendingResult: PostAttemptResponse | null;
  pendingOutcomeConfig: OutcomeConfig | null;
  attemptError: boolean;
  hasRemainingEntries: boolean;
  remainingEntries: number;
  wheelCanvasRef: React.MutableRefObject<WheelCanvasHandle | null>;
  spin: () => void;
  onWheelRest: () => void;
  dismissOutcome: () => void;
  startNewAttempt: () => void;
};

// ─── Dev mock (REACT_APP_MOCK_REWARDS=true) ───────────────────────────────────
// MOCK_REWARDS gates the SPIN-action mocking (skip POST/PUT, synthesize a
// pre-determined outcome). The DATA side (config/status) is sourced from
// `RewardsEnrollmentProvider`, which seeds shared mocks when
// `env.rewardsMockEnabled` is on. The build-time `REACT_APP_MOCK_REWARDS` flag
// stays useful for local dev without window.env injection.
const MOCK_REWARDS =
  (process.env.NODE_ENV !== 'production' &&
    process.env.REACT_APP_MOCK_REWARDS === 'true') ||
  env.rewardsMockEnabled;

// Cycles through outcomes on each mock spin so all segments are exercisable.
let mockOutcomeCursor = 0;

export function useSpinWheel(): UseSpinWheelReturn {
  // user is guaranteed non-null here: SpinWheel only renders inside PrivateRoute,
  // which shows a loading page until the Auth0 + GraphQL user fetch completes.
  const { user } = useAuth0();
  const agentId = user!.id;

  // Shared 26for26 spin data from the app-root provider — populated by the
  // landing page's mount, by this hook's mount, or both (idempotent). Reading
  // from context here eliminates the redundant /config/current and /status
  // fetches that this hook used to issue on its own.
  const {
    config: configFromContext,
    status: statusFromContext,
    pendingAttempt,
    setPendingAttempt,
    rewardsDataLoading,
    rewardsDataError,
    loadRewardsData,
    refetchStatus: refetchStatusFromContext,
  } = useRewardsEnrollmentContext();

  // Mount-time idempotent loader: no-op if landing already primed the data;
  // fetches as a fallback on direct deep-link / hard refresh on /rewards/spin.
  useEffect(() => {
    loadRewardsData();
  }, [loadRewardsData]);

  const [spinState, setSpinState] = useState<SpinState>('loading');
  const [
    pendingResult,
    setPendingResult,
  ] = useState<PostAttemptResponse | null>(null);
  const [
    pendingOutcomeConfig,
    setPendingOutcomeConfig,
  ] = useState<OutcomeConfig | null>(null);
  const [attemptError, setAttemptError] = useState(false);
  const wheelCanvasRef = useRef<WheelCanvasHandle | null>(null);

  // ── Attempt (POST; creates a PENDING attempt + pre-determined outcome) ────
  // Fires on the landing page's "SPIN NOW" button for the first spin, and
  // again here from "Play Another Entry" so the agent stays on the wheel
  // screen across consecutive spins. Entry isn't consumed until PUT /accept.
  const [, executeNewAttempt] = useLazyIncentiveProgramsApi<
    PostAttemptResponse
  >({
    method: 'POST',
    url: `/agents/${agentId}/attempts`,
  });

  // ── Accept (PUT; commits the PENDING attempt and awards the payout) ───────
  // Two-phase pattern per prime-app docs/26for26/journeys/03-agent-spins-the-wheel:
  //  - POST /attempts fires on the OUTER button (landing page "SPIN NOW")
  //    or "Play Another Entry" and returns a PENDING attempt with a
  //    pre-determined outcome. Entry stays AVAILABLE.
  //  - The INNER Spin button on the wheel screen fires PUT /accept as
  //    fire-and-forget, then animates the wheel to the known outcome
  //    immediately without blocking on the backend.
  const [, executeAccept] = useLazyIncentiveProgramsApi<unknown>({
    method: 'PUT',
  });

  const acceptAttempt = useCallback(
    (attemptId: string | null) => {
      if (MOCK_REWARDS || !attemptId) return;
      executeAccept({
        url: `/agents/${agentId}/attempts/${attemptId}/accept`,
      }).catch(() => {
        // Accept is idempotent on the backend — the next visit reuses the
        // PENDING attempt via the idempotent POST path. Emit an event so
        // repeated failures are observable.
        reportEvent('26for26-spin-wheel:accept-failed');
      });
    },
    [agentId, executeAccept]
  );

  const config = configFromContext;
  const status = statusFromContext;
  const dataLoading = rewardsDataLoading;
  const dataError = rewardsDataError;

  // Pluck the active TRACK1 phase's outcomes into the wheel's internal shape.
  // The status response carries the currently-active phase code; mirror that
  // back into the program config tree. Memoized so the array identity is
  // stable across renders — the useCallback below depends on it.
  const outcomes: OutcomeConfig[] = useMemo(
    () => (config && status ? outcomesForPhase(config, status.phase.code) : []),
    [config, status]
  );

  // ── Transition out of 'loading' once initial data is available ─────────────
  useEffect(() => {
    if (dataLoading) return;
    if (dataError) {
      setSpinState('error');
      return;
    }
    if (!config || !status) return;

    // Only drive the transition out of 'loading'. Any other state was set
    // deliberately by spin/dismissOutcome and must not be overridden here.
    if (spinState !== 'loading') return;

    // Expected happy-path entry: the landing page's SPIN NOW button has just
    // fired POST /attempts and seeded `pendingAttempt` in context — hydrate
    // local state so the wheel knows which segment to animate to when the
    // agent clicks the inner Spin button. Stay in 'idle' (not 'revealed') —
    // the outcome reveal only happens after the inner Spin click triggers
    // accept + animation.
    //
    // Fallback: deep-link / hard-refresh case where context has no pending
    // attempt. Re-derive from /status's hasActiveAttempt + lastResult so the
    // mid-flow reconnect UX still works.
    let pending: PostAttemptResponse | null = pendingAttempt;
    if (!pending && status.hasActiveAttempt && status.lastResult) {
      pending = {
        // activeAttemptId may be null if status briefly reports hasActiveAttempt
        // before the attempt row id surfaces. Empty string keeps the response
        // type happy; acceptAttempt's null/empty guard skips the PUT so we
        // don't fire an invalid accept URL.
        rewardAttemptId: status.activeAttemptId ?? '',
        status: 'PENDING',
        outcome: status.lastResult.outcome,
        prizeCode: status.lastResult.prizeCode,
        prizeAmount: status.lastResult.prizeAmount,
        payoutStatus: null,
      };
    }

    if (pending) {
      const outcomeIndex = outcomes.findIndex(
        o =>
          o.code === pending!.outcome && o.prizeAmount === pending!.prizeAmount
      );
      setPendingResult(pending);
      setPendingOutcomeConfig(
        outcomeIndex >= 0 ? outcomes[outcomeIndex] : null
      );
      setSpinState('idle');
      return;
    }

    setSpinState(status.availableEntries > 0 ? 'idle' : 'no_entries');
  }, [
    config,
    dataError,
    dataLoading,
    outcomes,
    pendingAttempt,
    spinState,
    status,
  ]);

  // ── Spin (inner button — accepts the pre-determined outcome) ──────────────
  // By the time the agent clicks Spin here, POST /attempts has already run on
  // the landing page and the outcome is in `pendingResult` (hydrated by the
  // effect above). This handler fires PUT /accept fire-and-forget so the
  // backend consumes the entry + creates the payout, then animates the wheel
  // to the pre-determined segment without blocking on the accept response.
  const spin = useCallback(() => {
    if (spinState !== 'idle' || !config) return;
    setAttemptError(false);
    setSpinState('spinning');

    if (MOCK_REWARDS) {
      // Local-only path: cycle through outcomes deterministically for visual
      // testing. Real backend interaction is stubbed.
      const currentIndex = mockOutcomeCursor % outcomes.length;
      const outcome = outcomes[currentIndex];
      mockOutcomeCursor += 1;
      const mockResult: PostAttemptResponse = {
        rewardAttemptId: `mock-attempt-${mockOutcomeCursor}`,
        status: 'DECIDED',
        outcome: outcome.code,
        prizeCode: outcome.isWin ? outcome.code : null,
        prizeAmount: outcome.prizeAmount,
        payoutStatus: null,
      };
      setPendingResult(mockResult);
      setPendingOutcomeConfig(outcome);
      const segmentIndexes = findMatchingSegmentIndexes(outcomes, currentIndex);
      const targetSegmentIndex =
        segmentIndexes[Math.floor(Math.random() * segmentIndexes.length)] ?? 0;
      wheelCanvasRef.current?.spinToItem(targetSegmentIndex);
      return;
    }

    // Real path: the PENDING attempt was created by the landing-page POST.
    // If it's missing here (agent deep-linked to /rewards/spin without going
    // through SPIN NOW, or status hadn't settled), bail with an error state.
    if (!pendingResult || !pendingOutcomeConfig) {
      setSpinState('idle');
      setAttemptError(true);
      reportEvent('26for26-spin-wheel:accept-without-pending');
      return;
    }

    // Fire-and-forget accept. Errors are logged inside acceptAttempt and the
    // animation continues either way — the outcome is already decided.
    acceptAttempt(pendingResult.rewardAttemptId);

    const outcomeIndex = outcomes.findIndex(
      o =>
        o.code === pendingOutcomeConfig.code &&
        o.prizeAmount === pendingOutcomeConfig.prizeAmount
    );
    const targetPrizeIndex = outcomeIndex >= 0 ? outcomeIndex : 0;
    const segmentIndexes = findMatchingSegmentIndexes(
      outcomes,
      targetPrizeIndex
    );
    const targetSegmentIndex =
      segmentIndexes[Math.floor(Math.random() * segmentIndexes.length)] ?? 0;
    wheelCanvasRef.current?.spinToItem(targetSegmentIndex);
  }, [
    acceptAttempt,
    config,
    outcomes,
    pendingOutcomeConfig,
    pendingResult,
    spinState,
  ]);

  // ── Wheel rest callback (fires when the spin animation completes) ──────────
  const onWheelRest = useCallback(() => {
    setSpinState('revealed');
  }, []);

  // ── Centralised remaining-entry count ─────────────────────────────────────
  // Single source of truth used both in dismissOutcome and exposed to callers,
  // so JSX and state-machine logic always agree on whether entries remain.
  const remainingEntries = Math.max(0, (status?.availableEntries ?? 0) - 1);
  const hasRemainingEntries = remainingEntries > 0;

  // ── Dismiss outcome modal ─────────────────────────────────────────────────
  const dismissOutcome = useCallback(() => {
    setPendingResult(null);
    setPendingOutcomeConfig(null);
    // Clear the shared context's pending attempt so a re-mount of either
    // landing or wheel doesn't re-hydrate a stale outcome.
    setPendingAttempt(null);
    // Optimistic: decrement by the entry we just consumed so the next state
    // is correct without waiting for the refetch.
    setSpinState(hasRemainingEntries ? 'idle' : 'no_entries');
    // Background refresh to sync entry count with the server.
    if (!MOCK_REWARDS) refetchStatusFromContext();
  }, [hasRemainingEntries, refetchStatusFromContext, setPendingAttempt]);

  // ── Start a new attempt (Play Another Entry) ─────────────────────────────
  // Called from the outcome modal's "Play Another Entry" button. Matches the
  // landing-page SPIN NOW path — POST a new PENDING attempt with a
  // pre-determined outcome and hydrate local state so the inner Spin button
  // can accept it. Keeps the agent on the wheel screen across spins.
  const startNewAttempt = useCallback(() => {
    // Clear the previous spin's modal state immediately so the wheel screen
    // renders in 'loading' while the POST is in flight.
    setPendingResult(null);
    setPendingOutcomeConfig(null);
    setAttemptError(false);
    // Use 'posting' rather than 'loading' so the initial-data useEffect
    // (which guards on spinState === 'loading') doesn't re-fire with stale
    // status while the POST is in flight.
    setSpinState('posting');

    if (MOCK_REWARDS) {
      // No backend in mock mode — drop straight to idle and let the inner
      // Spin button synthesise the mock outcome.
      setSpinState('idle');
      return;
    }

    executeNewAttempt({ data: {} })
      .then(({ data: result }) => {
        const outcomeIndex = outcomes.findIndex(
          o => o.code === result.outcome && o.prizeAmount === result.prizeAmount
        );
        const pending: PostAttemptResponse = {
          rewardAttemptId: result.rewardAttemptId,
          status: 'PENDING',
          outcome: result.outcome,
          prizeCode: result.prizeCode,
          prizeAmount: result.prizeAmount,
          payoutStatus: null,
        };
        setPendingResult(pending);
        // Mirror into the shared context so a navigation back-and-forth to
        // the landing screen keeps the outcome in sync.
        setPendingAttempt(pending);
        setPendingOutcomeConfig(
          outcomeIndex >= 0 ? outcomes[outcomeIndex] : null
        );
        setSpinState('idle');
        // Refresh entry count so "N spins available" drops to reflect that
        // this PENDING reserves the next one.
        refetchStatusFromContext();
      })
      .catch(() => {
        setAttemptError(true);
        setSpinState('idle');
      });
  }, [
    executeNewAttempt,
    outcomes,
    refetchStatusFromContext,
    setPendingAttempt,
  ]);

  return {
    config,
    outcomes,
    status,
    spinState,
    pendingResult,
    pendingOutcomeConfig,
    attemptError,
    hasRemainingEntries,
    remainingEntries,
    wheelCanvasRef,
    spin,
    onWheelRest,
    dismissOutcome,
    startNewAttempt,
  };
}
